import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import ABCDESection from '@/components/ABCDESection';
import { Wind, Heart, Brain, Thermometer, FileText } from 'lucide-react';

const sectionsConfig = [
  {
    id: 'airway',
    label: 'A - Airway',
    title: 'Atemweg',
    description: 'Beurteilung der Atemwege',
    icon: Wind,
    color: 'text-blue-600',
    normalText: 'Atemweg frei und ohne Verlegung, keine Fremdkörper erkennbar, kein Zungenbiss, SH rosig',
    options: [
      { id: 'airway_endangered', label: 'Atemwege gefährdet' },
      { id: 'partially_blocked', label: 'Teilverlegt' },
      { id: 'blocked', label: 'Verlegt' },
      { id: 'foreign_body', label: 'Fremdkörper im Atemweg' },
      { id: 'intubated', label: 'Intubiert' },
      { id: 'tracheostomy', label: 'Trachealkanüle vorhanden' },
      { id: 'mucosa_pale', label: 'Schleimhäute blass' },
      { id: 'mucosa_cyanotic', label: 'Schleimhäute zyanotisch' }
    ]
  },
  {
    id: 'breathing',
    label: 'B - Breathing',
    title: 'Atmung',
    description: 'Atemfrequenz und -qualität',
    icon: Wind,
    color: 'text-teal-600',
    normalText: 'Spontanatmung vorhanden und regelrecht, Atemfrequenz normwertig, Thoraxexkursionen seitengleich, vesikuläres Atemgeräusch beidseits, keine Dyspnoe, keine Zyanose, kein Einsatz der Atemhilfsmuskulatur, Sättigung: XX unter Raumluft',
    options: [
      { id: 'increased_rate', label: 'Erhöhte Atemfrequenz (Tachypnoe)' },
      { id: 'decreased_rate', label: 'Verlangsamte Atemfrequenz (Bradypnoe)' },
      { id: 'hyperventilation', label: 'Hyperventilation' },
      { id: 'dyspnea_mild', label: 'Dyspnoe (leicht)' },
      { id: 'dyspnea_moderate', label: 'Dyspnoe (mittel)' },
      { id: 'dyspnea_severe', label: 'Dyspnoe (schwer)' },
      { id: 'cyanosis_yes', label: 'Zyanose vorhanden' },
      { id: 'vesicular_left', label: 'Atemgeräusch nur links' },
      { id: 'vesicular_right', label: 'Atemgeräusch nur rechts' },
      { id: 'auxiliary_muscles_yes', label: 'Einsatz Atemhilfsmuskulatur' },
      { id: 'stridor', label: 'Stridor' },
      { id: 'rhonchi', label: 'Rasselgeräusche/Giemen' }
    ],
    inputs: [
      { id: 'spo2', label: 'SpO₂ (%)', type: 'number', placeholder: '98', min: 0, max: 100, default: 98 },
      { id: 'respiratory_rate', label: 'Atemfrequenz (/min)', type: 'number', placeholder: '16', min: 0, max: 60, default: 16 }
    ]
  },
  {
    id: 'circulation',
    label: 'C - Circulation',
    title: 'Kreislauf',
    description: 'Herzkreislauf-Parameter',
    icon: Heart,
    color: 'text-red-600',
    normalText: 'Kreislauf stabil, Puls kräftig und regelmäßig tastbar, Hautkolorit rosig',
    options: [
      { id: 'pulse_irregular', label: 'Puls unregelmäßig' },
      { id: 'pulse_weak', label: 'Puls schwach tastbar' },
      { id: 'tachycardia', label: 'Tachykardie (Puls > 100/min)' },
      { id: 'bradycardia', label: 'Bradykardie (Puls < 60/min)' },
      { id: 'hypertension', label: 'Hypertonie (erhöhter Blutdruck)' },
      { id: 'hypotension', label: 'Hypotonie (niedriger Blutdruck)' },
      { id: 'skin_pale', label: 'Haut blass' },
      { id: 'skin_mottled', label: 'Haut marmoriert' },
      { id: 'skin_cyanotic', label: 'Haut zyanotisch' },
      { id: 'cap_refill_over2', label: 'Rekapillarisierungszeit > 2s' },
      { id: 'bleeding_yes', label: 'Blutung vorhanden' },
      { id: 'cold_extremities', label: 'Kalte Extremitäten' },
      { id: 'jugular_distended', label: 'Gestaute Halsvenen' },
      { id: 'edema_present', label: 'Periphere Ödeme' },
      { id: 'chest_pain', label: 'Brustschmerzen' }
    ],
    ekgOptions: [
      { id: 'sinus_rhythm', label: 'EKG: Sinusrhythmus' },
      { id: 'atrial_fibrillation', label: 'EKG: Vorhofflimmern' },
      { id: 'stemi', label: 'EKG: STEMI' },
      { id: 'nstemi', label: 'EKG: NSTEMI-Verdacht' },
      { id: 'st_elevation', label: 'EKG: ST-Hebungen' },
      { id: 'st_depression', label: 'EKG: ST-Senkungen' },
      { id: 'rsb', label: 'EKG: Rechtsschenkelblock (RSB)' },
      { id: 'lsb', label: 'EKG: Linksschenkelblock (LSB)' },
      { id: 'av_block', label: 'EKG: AV-Block' },
      { id: 'extrasystoles', label: 'EKG: Extrasystolen' },
      { id: 'pacemaker', label: 'EKG: Schrittmacherrhythmus' }
    ],
    inputs: [
      { id: 'bp_systolic', label: 'RR systolisch (mmHg)', type: 'number', placeholder: '120', min: 50, max: 250, default: 120 },
      { id: 'bp_diastolic', label: 'RR diastolisch (mmHg)', type: 'number', placeholder: '80', min: 30, max: 150, default: 80 },
      { id: 'heart_rate', label: 'Herzfrequenz (/min)', type: 'number', placeholder: '75', min: 30, max: 200, default: 75 }
    ]
  },
  {
    id: 'disability',
    label: 'D - Disability',
    title: 'Neurologie',
    description: 'Neurologische Beurteilung',
    icon: Brain,
    color: 'text-purple-600',
    normalText: 'Patient wach, ansprechbar und POST orientiert, GCS 15, Pupillen rund, mittelweit, isokor und prompt lichtreagibel beidseits, keine Lähmungserscheinungen, keine Sensibilitätsstörungen, kein Krampfgeschehen, BEFAST: unauffällig, keine Amnesie',
    options: [
      { id: 'pupils_anisokor', label: 'Pupillen anisokor' },
      { id: 'pupils_not_reactive', label: 'Pupillen nicht lichtreagibel' },
      { id: 'seizure_active', label: 'Krampfgeschehen (aktuell krampfend)' },
      { id: 'seizure_past', label: 'Stattgehabter Krampfanfall' },
      { id: 'confusion', label: 'Verwirrtheit' },
      { id: 'unconscious', label: 'Bewusstlos' },
      { id: 'paralysis', label: 'Lähmungserscheinungen' },
      { id: 'hypoglycemia', label: 'BZ-Entgleisung: Hypoglykämie' },
      { id: 'hyperglycemia', label: 'BZ-Entgleisung: Hyperglykämie' }
    ],
    inputs: [
      { id: 'gcs', label: 'GCS', type: 'number', placeholder: '15', max: 15, min: 3, default: 15 },
      { id: 'blood_sugar', label: 'Blutzucker (mg/dl)', type: 'number', placeholder: '95', min: 20, max: 600, default: 95 }
    ],
    orientationOptions: [
      { id: 'oriented_place', label: 'Örtlich orientiert', default: true },
      { id: 'oriented_time', label: 'Zeitlich orientiert', default: true },
      { id: 'oriented_person', label: 'Zur Person orientiert', default: true },
      { id: 'oriented_situation', label: 'Situativ orientiert', default: true }
    ],
    befastOptions: [
      { id: 'balance', label: 'Balance', default: true },
      { id: 'eyes', label: 'Eyes (Sehen)', default: true },
      { id: 'face', label: 'Face (Gesicht)', default: true },
      { id: 'arms', label: 'Arms (Arme)', default: true },
      { id: 'speech', label: 'Speech (Sprache)', default: true }
    ],
    befastInput: {
      id: 'symptom_onset',
      label: 'Time: Symptombeginn',
      type: 'text',
      placeholder: 'z.B. vor 2 Stunden, 14:30 Uhr'
    }
  },
  {
    id: 'exposure',
    label: 'E - Exposure',
    title: 'Untersuchung',
    description: 'Körperliche Untersuchung',
    icon: Thermometer,
    color: 'text-orange-600',
    normalText: 'Keine Verletzung, keine Schmerzen, keine Übelkeit, kein Erbrechen, Körpertemperatur normal, kein Infekt',
    options: [
      { id: 'injuries_yes', label: 'Verletzungen sichtbar' },
      { id: 'rash', label: 'Ausschläge vorhanden' },
      { id: 'swelling', label: 'Schwellungen vorhanden' },
      { id: 'deformity', label: 'Deformitäten' },
      { id: 'bruising', label: 'Hämatome' },
      { id: 'nausea', label: 'Übelkeit' },
      { id: 'vomiting', label: 'Erbrechen' },
      { id: 'vertigo_rotational', label: 'Drehschwindel' },
      { id: 'vertigo_non_rotational', label: 'Schwankschwindel' }
    ],
    inputs: [
      { id: 'temperature', label: 'Temperatur (°C)', type: 'number', step: '0.1', placeholder: '36.8', min: 30, max: 43, default: 36.8 },
      { id: 'pain_location', label: 'Schmerzlokalisation', type: 'text', placeholder: 'z.B. Thorax' },
      { id: 'pain_scale', label: 'Schmerzskala (0-10)', type: 'number', placeholder: '0', max: 10, min: 0, default: 0 }
    ]
  }
];

export const ABCDESections = ({ data, onChange }) => {
  const handleSectionChange = (sectionId, sectionData) => {
    onChange({
      ...data,
      [sectionId]: sectionData
    });
  };

  return (
    <Card className="section-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-accent/10 rounded-lg">
            <FileText className="w-5 h-5 text-accent" />
          </div>
          <div>
            <CardTitle className="text-2xl">ABCDE-Schema</CardTitle>
            <CardDescription className="text-base mt-1">
              Strukturierte Patientenbeurteilung (Standardmäßig alle Werte unauffällig)
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="airway" className="w-full">
          <TabsList className="grid w-full grid-cols-5 h-auto gap-1 sm:gap-2 bg-muted/50 p-1 sm:p-2 overflow-x-auto">
            {sectionsConfig.map((section) => (
              <TabsTrigger
                key={section.id}
                value={section.id}
                data-testid={`abcde-${section.id}-tab`}
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground py-2 sm:py-3 min-w-[60px] sm:min-w-0"
              >
                <div className="flex flex-col items-center gap-0.5 sm:gap-1">
                  <section.icon className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="text-[10px] sm:text-xs font-semibold">{section.label.split(' - ')[0]}</span>
                </div>
              </TabsTrigger>
            ))}
          </TabsList>

          {sectionsConfig.map((section) => (
            <TabsContent key={section.id} value={section.id} className="mt-6">
              <ABCDESection
                section={section}
                data={data[section.id]}
                onChange={(sectionData) => handleSectionChange(section.id, sectionData)}
              />
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ABCDESections;
